import { useState } from "react";
import { useMediaQuery } from "@mui/material";
import Select from "@rio-cloud/rio-uikit/Select";
import DatePicker from "@rio-cloud/rio-uikit/DatePicker";
import { BudgetStatusFilterEnum } from "../../enums/budgetStatus";
import "./filters.scss";

interface FiltersProps {
  onCreateBudget: () => void;
  onChassisSearch: (chassis: string) => void;
  onStatusChange: (status?: BudgetStatusFilterEnum) => void;
  onDateRangeChange: (startDate?: string, endDate?: string) => void;
  persona?: string;
}

const Filters = ({
  onCreateBudget,
  onChassisSearch,
  onStatusChange,
  onDateRangeChange,
  persona,
}: FiltersProps) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isTablet = useMediaQuery("(max-width: 1024px) and (min-width: 769px)");
  const [chassis, setChassis] = useState<string>("");
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);

  const parseYMD = (dateLocal?: string) => {
    if (!dateLocal) return undefined;
    const [year, month, day] = dateLocal.split("-").map(Number);
    if (!year || !month || !day) return undefined;
    return new Date(year, month - 1, day);
  };

  const handleChassisChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setChassis(value);
    onChassisSearch(value);
  };

  const handleStatusChange = (value: BudgetStatusFilterEnum | undefined) => {
    setStatus(value);
    onStatusChange(value);
  };

  const handleStartDateChange = (e: any) => {
    if (typeof e === 'string' && e.length) return;
    if (typeof e === 'string') {
      setStartDate(undefined);
      onDateRangeChange(undefined, endDate);
      return;
    }
    const newStartDate = e?.toISOString('YYYY-MM-DD')?.split('T')[0];
    setStartDate(newStartDate);
    onDateRangeChange(newStartDate, endDate);
  };

  const handleEndDateChange = (e: any) => {
    if (typeof e === 'string' && e.length) return;
    if (typeof e === 'string') {
      setEndDate(undefined);
      onDateRangeChange(startDate, undefined);
      return;
    }
    const newEndDate = e?.toISOString('YYYY-MM-DD')?.split('T')[0];
    setEndDate(newEndDate);
    onDateRangeChange(startDate, newEndDate);
  };

  return (
    <div className="flex flex-col gap-4 mb-4 w-full">
      <div className={`flex ${isMobile ? "flex-col gap-3" : "flex-row gap-6"} w-full`}>
        <div className={`flex flex-col ${isMobile ? "mt-0" : "mt-9"} ${persona !== 'CONSULTANT' ? 'display-none' : ''}`}>
          <button
            type="button"
            className={`btn bg-primary flex items-center justify-center gap-2 ${isMobile ? "w-full" : ""}`}
            onClick={onCreateBudget}
          >
            <span className="rioglyph rioglyph-plus"></span>
            {isMobile ? "Criar" : "Criar orçamento"}
          </button>
        </div>

        <div className={`form-group ${isMobile ? "w-full" : "flex-1"} ${isMobile ? "mt-0" : "mt-9"}`}>          
          <div className="input-group">
            <span className="input-group-addon" style={{ display: "flex", alignItems: "center" }}>
              <span className="rioglyph rioglyph-search" />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Busque por chassi"
              id="chassisSearch"
              value={chassis}
              onChange={handleChassisChange}
              maxLength={17}
              style={{ height: "34px", minHeight: "34px", maxHeight: "34px", lineHeight: "34px" }}
            />
          </div>
        </div>

        <div className={`form-group ${isMobile ? "w-full" : "flex-shrink-0"} ${isMobile ? "mt-0" : ""}`}>
          <label htmlFor="statusFilter" className="text-sm font-medium mb-1 block h-[20px]">
            Status
          </label>
          <div style={{ height: "34px", minHeight: "34px", maxHeight: "34px" }}>
            <Select
              id="statusFilter"
              options={[
                { id: '', label: 'Todos' },
                { id: BudgetStatusFilterEnum.SENT, label: 'Enviados para análise' },
                { id: BudgetStatusFilterEnum.UPDATED, label: 'Alterados' },
                { id: BudgetStatusFilterEnum.COUNTER_PROPOSED, label: 'Contraproposta' },
                { id: BudgetStatusFilterEnum.APPROVED_BY_FACTORY, label: 'Aprovados pelo Volks|total' },
                { id: BudgetStatusFilterEnum.REJECTED, label: 'Recusado' },
                { id: BudgetStatusFilterEnum.APPROVED, label: 'Aprovados pelo cliente' },
              ]}
              value={[status ?? '']}
              onChange={(e) => handleStatusChange(e?.id as BudgetStatusFilterEnum)}
              className={isMobile ? "w-full" : "min-w-[150px]"}
            />
          </div>
        </div>

        <div className={`form-group ${isMobile ? "w-full" : "flex-shrink-0"} ${isMobile ? "mt-0" : ""}`}>
          <label className="text-sm font-medium mb-1 block h-[20px]">Data de atualização</label>
          <div className={`flex items-center ${isMobile ? "flex-col gap-2" : "flex-row gap-3"}`} style={{ height: isMobile ? "auto" : "34px", minHeight: isMobile ? "auto" : "34px", maxHeight: isMobile ? "auto" : "34px" }}>
            <div className={isMobile ? "w-full" : ""} style={{ minWidth: isMobile ? "100%" : "180px", height: isMobile ? "auto" : "34px", minHeight: isMobile ? "auto" : "34px", maxHeight: isMobile ? "auto" : "34px" }}>
              <DatePicker
                id="updateDateStart"
                value={startDate ? parseYMD(startDate) : undefined}
                onChange={handleStartDateChange}
                locale="pt-BR"
                dateFormat="DD-MM-YYYY"
                timeFormat={false}
                mandatory={false}
                clearableInput={true}
                className="date-picker-large"
              />
            </div>
            <span className={`text-sm ${isMobile ? "hidden" : "whitespace-nowrap"}`} style={{ lineHeight: "34px" }}>Até</span>
            <div className={`budget-datepicker-end ${isMobile ? "w-full" : ""}`} style={{ minWidth: isMobile ? "100%" : "180px", height: isMobile ? "auto" : "34px", minHeight: isMobile ? "auto" : "34px", maxHeight: isMobile ? "auto" : "34px" }}>
              <DatePicker
                id="updateDateEnd"
                value={endDate ? parseYMD(endDate) : undefined}
                onChange={handleEndDateChange}
                locale="pt-BR"
                dateFormat="DD-MM-YYYY"
                timeFormat={false}
                mandatory={false}
                clearableInput={true}
                className="date-picker-large"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Filters;
